import Renderer from "src/lib/renderer";
import { Box, IconButton, VStack, Container } from "@chakra-ui/react";
import { FC, useState, useRef } from "react";
import { BlockContainer, ShiIcon } from "src/atoms";
import { GatsbyImage } from "gatsby-plugin-image";

import { heading2, heading3 } from "src/lib/renderer/elements/headings";
import { Element } from "@prismicio/react";

import YouTube from "react-youtube";
import paragraph from "src/lib/renderer/elements/paragraph";

import { getVideoType, getVimeoId, getLoomId, getVimeoParams } from "./utils";
import EmbedPlayer from "./components/EmbedPlayer";
import NativeVideoPlayer from "./components/NativeVideoPlayer";
import UnsupportedVideoFallback from "./components/UnsupportedVideoFallback";

// Shared CSS class applied to every player so they all fill the
// 16:9 aspect-ratio wrapper identically.
const VIDEO_CLASS = "videoblock-video";

const BlockVideo: FC<Gatsby.PageBlockVideoFragment> = ({ primary }) => {
  const [play, setPlay] = useState(false);
  const [youtubePlayer, setYoutubePlayer] = useState<any>(null);
  const nativeVideoRef = useRef<HTMLVideoElement>(null);

  if (!primary) throw Error();

  const { content, buttons, youtube_id, video_url, thumbnail } = primary;

  const videoType = getVideoType(youtube_id, video_url);

  const handlePlayClick = () => {
    setPlay(true);
    if (youtubePlayer) {
      youtubePlayer.playVideo();
    }
    if (nativeVideoRef.current) {
      nativeVideoRef.current.play().catch(() => {});
    }
  };

  const hasThumbnail = !!thumbnail?.document;

  // ---------------------------------------------------------------------------
  // Render the correct player for the detected video type
  // ---------------------------------------------------------------------------
  const renderPlayer = () => {
    switch (videoType.kind) {
      case "youtube":
        return (
          <YouTube
            videoId={videoType.id}
            className={VIDEO_CLASS}
            onPlay={() => setPlay(true)}
            onEnd={() => setPlay(false)}
            onReady={(event) => setYoutubePlayer(event.target)}
            opts={{
              playerVars: {
                modestbranding: 1, // removes YouTube logo in controls
              },
            }}
          />
        );

      case "vimeo": {
        const vimeoId = getVimeoId(videoType.url);
        if (!vimeoId) return null;

        const extraParams = getVimeoParams(videoType.url, vimeoId);
        const searchParams = `?dnt=1&title=0&byline=0&portrait=0${extraParams}`;

        const vimeoEmbedUrl = `https://player.vimeo.com/video/${vimeoId}${searchParams}`;
        const urlWithAutoplay = `${vimeoEmbedUrl}&autoplay=1`;

        // If there's a thumbnail, don't load the iframe until they click play (Facade)
        if (hasThumbnail && !play) return null;

        return (
          <EmbedPlayer
            embedUrl={hasThumbnail ? urlWithAutoplay : vimeoEmbedUrl}
            title="Vimeo video player"
            className={VIDEO_CLASS}
          />
        );
      }

      case "loom": {
        const loomId = getLoomId(videoType.url);
        if (!loomId) return null;
        const loomEmbedUrl = `https://www.loom.com/embed/${loomId}?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true`;
        const urlWithAutoplay = `${loomEmbedUrl}${play ? "&autoplay=1" : ""}`;

        // If there's a thumbnail, don't load the iframe until they click play (Facade)
        if (hasThumbnail && !play) return null;

        return (
          <EmbedPlayer
            embedUrl={hasThumbnail ? urlWithAutoplay : loomEmbedUrl}
            title="Loom video player"
            className={VIDEO_CLASS}
          />
        );
      }

      case "mp4":
        return (
          <NativeVideoPlayer
            ref={nativeVideoRef}
            url={videoType.url}
            className={VIDEO_CLASS}
          />
        );

      case "unsupported":
        return <UnsupportedVideoFallback url={videoType.url} />;

      case "none":
      default:
        return null;
    }
  };

  return (
    <BlockContainer>
      <Container variant="heading">
        <VStack mx="auto" spacing="space-8" w="full">
          <Renderer
            field={content}
            overrides={
              {
                [Element.heading2]: (args: any) =>
                  heading2({
                    ...args,
                    overrideProps: {
                      marginBottom: 0,
                    },
                  }),
                [Element.heading3]: (args: any) =>
                  heading3({
                    ...args,
                    overrideProps: {
                      variant: "heading-4",
                      color: "monochrome-600",
                    },
                  }),
                [Element.paragraph]: (args: any) =>
                  paragraph({
                    ...args,
                    overrideProps: {
                      variant: "body-md-reg",
                    },
                  }),
              } as any
            }
          />
        </VStack>
        <Box mt="space-40" w="full">
          <Box
            width="full"
            paddingBottom="56.25%"
            position="relative"
            sx={{
              [`.${VIDEO_CLASS}`]: {
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                borderRadius: "intermediate",
              },
            }}
          >
            {renderPlayer()}

            {/* Facade: custom thumbnail + play button overlay for ALL video types */}
            {hasThumbnail && (
              <Box
                opacity={!play ? "1" : "0"}
                position="absolute"
                w="full"
                h="full"
                top="0"
                left="0"
                zIndex="1"
                pointerEvents={play ? "none" : "auto"}
                transitionProperty="all"
                transitionDuration="300ms"
                transitionTimingFunction="ease-out"
              >
                <Box w="full" h="full" position="relative">
                  {thumbnail?.document?.__typename ===
                    "PrismicElementImage" && (
                    <Box
                      w="full"
                      h="full"
                      as={GatsbyImage}
                      image={
                        thumbnail.document.data?.image_file?.gatsbyImageData
                      }
                      alt={thumbnail.document.data?.image_file?.alt ?? ""}
                      sx={{
                        img: {
                          borderRadius: "intermediate",
                        },
                      }}
                    />
                  )}
                  <IconButton
                    onClick={handlePlayClick}
                    aria-label="Play"
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    bg="transparent"
                    sx={{
                      ".play": {
                        path: {
                          fill: "grey-0",
                          _hover: {
                            fill: "grey-1",
                          },
                        },
                      },
                    }}
                    _hover={{ bg: "transparent" }}
                    icon={
                      <ShiIcon
                        name="play"
                        className="play"
                        w={{ base: "44px", md: "92px" }}
                        h={{ base: "44px", md: "92px" }}
                      />
                    }
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {buttons && buttons.text !== "" && (
          <Box mt="space-40">
            <Renderer field={buttons} />
          </Box>
        )}
      </Container>
    </BlockContainer>
  );
};

export default BlockVideo;
